import { useNavigate } from 'react-router-dom';
import { insightsData } from '../../../mocks/insights';
import { useState } from 'react';

export default function InsightsGrid() {
  const navigate = useNavigate();
  const [subscriptionCode, setSubscriptionCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const categories = [
    { id: 'core-loop', name: '히트 코어루프', color: '#8b4513', icon: 'ri-fire-fill', route: '/board/core-loop' },
    { id: 'fast-follower', name: '패스트 팔로워', color: '#2f4f4f', icon: 'ri-rocket-fill', route: '/board/fast-follower' },
    { id: 'idea-archive', name: '해든의 코어루프\n아이디어 아카이빙', color: '#5a4a3a', icon: 'ri-gamepad-fill', route: '/board/idea-archive' },
    { id: 'hit-prediction', name: '히트 가능성\n예측 리포트', color: '#4a3a5a', icon: 'ri-line-chart-fill', route: '/board/hit-prediction' }
  ];

  const handlePremiumClick = (postId: number) => {
    if (isUnlocked) {
      navigate(`/post/${postId}`);
    } else {
      setShowCodeInput(true);
    }
  };

  const handleCodeSubmit = () => {
    // 구독 코드: "해든"을 입력하면 잠금 해제
    if (subscriptionCode.trim() === '해든') {
      setIsUnlocked(true);
      setShowCodeInput(false);
      alert('프리미엄 콘텐츠가 잠금 해제되었습니다! 🎉');
    } else {
      alert('올바른 구독 코드를 입력해주세요.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {categories.map((category) => {
          const categoryPosts = insightsData.filter(post => post.category === category.id);
          const previewPosts = categoryPosts.slice(0, 4);
          const isPremium = category.id === 'hit-prediction';
          
          return (
            <div key={category.id} className="space-y-4">
              {/* Category Header */}
              <div className="pb-3 border-b-2 border-[#d4c4a8]">
                <h2 
                  className="text-xl font-light tracking-wide flex items-center gap-2 whitespace-pre-line"
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    color: category.color,
                    minHeight: '3.5rem'
                  }}
                >
                  <i className={`${category.icon} text-lg flex-shrink-0`}></i>
                  <span className="leading-tight">{category.name}</span>
                </h2>
              </div>

              {/* Posts List - Only show 4 */}
              <div className="space-y-3">
                {previewPosts.map((post) => (
                  <article
                    key={post.id}
                    onClick={() => {
                      if (isPremium && !isUnlocked) {
                        handlePremiumClick(post.id);
                      } else {
                        navigate(`/post/${post.id}`);
                      }
                    }}
                    className={`group cursor-pointer ${isPremium && !isUnlocked ? 'relative' : ''}`}
                  >
                    <div className={`bg-white/40 border border-[#e8dfd0] rounded-lg p-4 hover:bg-white/60 hover:border-[#d4c4a8] transition-all duration-300 ${isPremium && !isUnlocked ? 'opacity-75' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-[#9d8b6f]">{post.date}</span>
                        <span className="text-xs text-[#b5a89a]">·</span>
                        <span className="text-xs text-[#9d8b6f]">{post.readTime}</span>
                        {isPremium && !isUnlocked && (
                          <span className="ml-auto px-2 py-0.5 bg-[#4a3a5a] text-white text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-vip-crown-fill"></i>
                            프리미엄
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-normal text-[#4a3f33] mb-2 leading-relaxed group-hover:text-[#6b5d4f] transition-colors duration-200">
                        {post.title}
                      </h3>
                      <p className="text-sm text-[#8b7d6f] leading-relaxed line-clamp-2">
                        {post.excerpt}
                      </p>
                    </div>
                  </article>
                ))}
              </div>

              {/* View More Button */}
              {categoryPosts.length > 4 && (
                <button
                  onClick={() => navigate(category.route)}
                  className="w-full py-3 bg-white/60 border border-[#e8dfd0] rounded-lg text-[#6b5d4f] hover:bg-white hover:border-[#d4c4a8] transition-all duration-300 text-sm font-light flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  <span>더 보기</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Premium Code Input Modal */}
      {showCodeInput && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-[#f5f1e8] border-2 border-[#d4c4a8] rounded-lg p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4a3a5a] to-[#3a2a4a] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-line-chart-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-light text-[#4a3f33] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                히트 예측 리포트 구독
              </h3>
              <p className="text-sm text-[#6b5d4f]">
                구독 코드를 입력하시면 데이터 기반 히트 예측과<br />장르별 성공 확률 분석을 확인하실 수 있습니다
              </p>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={subscriptionCode}
                onChange={(e) => setSubscriptionCode(e.target.value)}
                placeholder="구독 코드를 입력하세요"
                className="w-full px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#4a3f33] placeholder-[#9d8b6f] focus:outline-none focus:border-[#4a3a5a] transition-colors text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCodeInput(false)}
                  className="flex-1 px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#6b5d4f] hover:bg-[#faf8f3] transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
                <button
                  onClick={handleCodeSubmit}
                  className="flex-1 px-4 py-3 bg-[#4a3a5a] text-white rounded-lg hover:bg-[#3a2a4a] transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  잠금 해제
                </button>
              </div>

              <div className="pt-4 border-t border-[#e8dfd0]">
                <p className="text-xs text-[#8b7d6f] text-center mb-3">
                  💎 프리미엄 구독 혜택
                </p>
                <ul className="text-xs text-[#6b5d4f] space-y-1 mb-3">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4a3a5a]"></i>
                    <span>AI 기반 히트 예측 리포트</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4a3a5a]"></i>
                    <span>장르별 성공 확률 분석</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4a3a5a]"></i>
                    <span>실패 사례 부검 리포트</span>
                  </li>
                </ul>
                <button className="w-full px-4 py-2 bg-gradient-to-r from-[#5a4a3a] to-[#4a3a2a] text-white rounded-lg hover:from-[#4a3a2a] hover:to-[#3a2a1a] transition-all duration-300 text-xs whitespace-nowrap cursor-pointer shadow-md">
                  프리미엄 구독 신청하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
